import type { OrderDetailsExtended } from 'app/src/model'
import type { refundApi } from 'app/src/services/welfareportal'
import type { Membership } from 'app/src/services/welfareportal/membership-api'
import type { DocumentInfo, InvoiceData } from 'app/src/services/welfareportal/refund-api'

/** Function for creating a new refund */
export const createNewRefund = (order: OrderDetailsExtended, membershipData: Membership) => {
  return {
    attributes: {
      limitType: order?.product?.serviceCategory, // TODO
      orderId: order?.orderId,
      subCategoryId: order?.variant?.externalId || order?.product?.externalId,
      userCampaignId: membershipData?.campaignId,
      userChannel: order?.product?.merchantType || 'welfareportal',
      userCompanyCode: membershipData?.companyCode,
      userEmail: order?.userInfo?.email,
      userFiscalCode: order?.userInfo?.fiscalCode,
      userMembershipId: order?.userInfo?.membershipId,
      userRegistrationNumber: membershipData?.userRegistrationNumber,
      // customerTaxId: order?.userInfo?.fiscalCode,
      userUpn: order?.userInfo?.email, // TODO replace with UPN from shoppingCollection when available
    },
    category: order?.variant?.variantName || order?.product?.productName, // TODO
    date: order?.orderDate,
    detailUrl: order?.detailPageUrl,
    documents: [],
    kind: order?.product?.productName,
    refundId: order?.orderId,
    // requestedTotal: 0,
    isCancelable: true,
    status: {
      updatedAt: order?.orderDate,
      value: 'DRAFT',
    },
    subject: {
      firstName: order?.userInfo?.firstName,
      fiscalCode: order?.userInfo?.fiscalCode,
      lastName: order?.userInfo?.lastName,
    },
    rule: {
      externalKeys: [
        /* they represent additional fields on refund form and they should be paired with invoice.data.[key].value */
        // {
        //   key: 'protocol',
        //   label: 'Numero Protocollo',
        //   format: 'number',
        //   tag: 'rimborso',
        // },
        // {
        //   key: 'fiscalCode',
        //   label: 'Codice Fiscale',
        //   format: 'string',
        //   tag: 'rimborso',
        // },
      ],
      additionalAttributes: {
        /* additional radio buttons */
        // radio: [
        //   {
        //     title: 'value1',
        //     attrs: [
        //       { name: 'name1', text: 'name1' },
        //       { name: 'name2', text: 'name2' },
        //     ],
        //   },
        // ],
      },
      allowedSubjects: ['dipendente', 'familiari'],
      documents: [
        {
          description: '\u003cp\u003e\u003c/p\u003e',
          extractionType: 'invoice',
          max: 1,
          min: 1,
          name: 'Invoice',
          types: ['invoice'],
        },
      ],
      kind: order?.product?.externalId,
      name: order?.product?.productName,
      period: ['2024 (fino al 20 novembre 2024)'],
      termsAndConditions: '',
    },
  }
}

/** Function for creating a document for refund invoice */
export const createDocumentRefund = (
  membershipData: Membership,
  refundConfirmRequest: refundApi.RefundConfirmRequest,
  refundId: string,
) => {
  const documentInfo: DocumentInfo = {
    dirty: false,
    entityId: refundId,
    fileUrl: '/pdf/PosteMondoWelfare-tabella-catalogo.pdf',
    id: crypto.randomUUID(),
    locked: false,
    mimeType: 'application/pdf',
    name: 'Fattura.pdf',
    scope: 'Refund',
    size: 217126,
    status: 'analyzed',
    type: 'invoice',
  }

  const documentInfoSignature =
    refundConfirmRequest?.invoice?.documentInfoSignature || 'T7U6dznhbzExNvCNm61UH2n56gTOdLQuR8WoqufxRRE'

  const dataSignature = refundConfirmRequest?.invoice?.dataSignature || 'iXHlMMK_nUpD84CtQh-mWmbsChcIhrpwp86aYXc7ITw'

  const metadata = refundConfirmRequest?.invoice?.metadata || {}

  const data: InvoiceData = {
    customerAddress: {
      city: {
        value: 'Roma',
        originalValue: 'Roma',
      },
      houseNumber: {
        value: '36',
        originalValue: '36',
      },
      postalCode: {
        value: '00144',
        originalValue: '00144',
      },
      road: {
        value: 'Viale Beethoven',
        originalValue: 'Viale Beethoven',
      },
      stateDistrict: {},
      streetAddress: {},
    },
    customerAddressRecipient: {
      value: membershipData?.userFirstName + ' ' + membershipData?.userLastName,
      originalValue: membershipData?.userFirstName + ' ' + membershipData?.userLastName,
    },
    customerName: {
      value: membershipData?.userFirstName + ' ' + membershipData?.userLastName,
      originalValue: membershipData?.userFirstName + ' ' + membershipData?.userLastName,
    },
    customerId: {
      value: membershipData?.membershipId,
      originalValue: membershipData?.membershipId,
    },
    customerTaxId: {
      value: membershipData?.userFiscalCode,
      originalValue: membershipData?.userFiscalCode,
    },
    dueDate: {},
    invoiceDate: {
      value: `${refundConfirmRequest?.invoice?.data?.invoiceDate?.value}`,
      originalValue: `${refundConfirmRequest?.invoice?.data?.invoiceDate?.value}`,
    },
    invoiceId: {
      value: refundConfirmRequest?.invoice?.data?.invoiceId?.value,
      originalValue: refundConfirmRequest?.invoice?.data?.invoiceId?.value,
    },
    invoiceTotal: {
      value: refundConfirmRequest?.invoice?.data?.invoiceTotal?.value || 0,
      originalValue: refundConfirmRequest?.invoice?.data?.invoiceTotal?.value || 0,
    },
    paymentTerm: {},
    subTotal: {},
    totalTax: {
      value: (refundConfirmRequest?.invoice?.data?.invoiceTotal?.value || 0) * 0.24,
      originalValue: (refundConfirmRequest?.invoice?.data?.invoiceTotal?.value || 0) * 0.24,
    },
    vendorAddress: {
      city: {
        value: 'Roma',
        originalValue: 'Roma',
      },
      houseNumber: {
        value: '175',
        originalValue: '175',
      },
      postalCode: {
        value: '00144',
        originalValue: '00144',
      },
      road: {
        value: 'Viale Europa',
        originalValue: 'Viale Europa',
      },
      stateDistrict: {},
      streetAddress: {},
    },
    vendorAddressRecipient: {
      value: refundConfirmRequest?.invoice?.data?.vendorName?.value,
      originalValue: refundConfirmRequest?.invoice?.data?.vendorName?.value,
    },
    vendorName: {
      value: refundConfirmRequest?.invoice?.data?.vendorName?.value,
      originalValue: refundConfirmRequest?.invoice?.data?.vendorName?.value,
    },
    vendorTaxId: {
      value: refundConfirmRequest?.invoice?.data?.vendorTaxId?.value,
      originalValue: refundConfirmRequest?.invoice?.data?.vendorTaxId?.value,
    },
  }

  return {
    data: data,
    dataSignature: dataSignature,
    documentInfo: documentInfo,
    documentInfoSignature: documentInfoSignature,
    metadata: metadata,
  }
}
